import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";

/**
 * Unified app state hook - single source of truth for app state checks.
 * Combines multiple Redux selectors into one cohesive view.
 */
export interface AppState {
    // Auth
    isLoggedIn: boolean;
    sessionExpired: boolean;
    currentUserId: string | null;

    // User memory (never cleared on logout)
    lastLoggedInEmail: string;

    // Onboarding
    onboardingCompleted: boolean;

    // App readiness
    isMatchesLoading: boolean;
    isAppReady: boolean;
}

export function useAppState(): AppState {
    const auth = useSelector((state: RootState) => state.auth);
    const user = useSelector((state: RootState) => state.user);
    const userActionsCheckList = useSelector(
        (state: RootState) => state.userActionsCheckList
    );
    const matchesLoading = useSelector(
        (state: RootState) => state.matches.loading
    );

    const isLoggedIn = auth.loggedIn;
    const onboardingCompleted = userActionsCheckList?.onboardingCompleted === true;

    return {
        isLoggedIn,
        sessionExpired: auth.sessionExpired || false,
        currentUserId: auth.session?.user_id || null,
        lastLoggedInEmail: user.usernameOrEmail || "",
        onboardingCompleted,
        isMatchesLoading: matchesLoading !== false,
        isAppReady: isLoggedIn && matchesLoading === false,
    };
}

// Convenience exports for common checks
export const useIsLoggedIn = () => useAppState().isLoggedIn;
export const useIsAppReady = () => useAppState().isAppReady;
export const useLastLoggedInEmail = () => useAppState().lastLoggedInEmail;
